import type { KadieChatState, LauncherPosition } from '../types/kadie';

const LAUNCHER_MARGIN = 8;
const LAUNCHER_SIZE = 56;

function launcherPositionKey(applicantId: string) {
  return `kadie-launcher-position:${applicantId}`;
}

function chatStateKey(applicantId: string) {
  return `kadie-chat:${applicantId}`;
}

function loginTimestampKey(applicantId: string) {
  return `kadie-login:${applicantId}`;
}

function previewStateKey(applicantId: string, loginTimestamp: number) {
  return `kadie-preview:${applicantId}:${loginTimestamp}`;
}

export function clampLauncherPosition(position: LauncherPosition): LauncherPosition {
  const maxRight = Math.max(LAUNCHER_MARGIN, window.innerWidth - LAUNCHER_SIZE - LAUNCHER_MARGIN);
  const maxBottom = Math.max(LAUNCHER_MARGIN, window.innerHeight - LAUNCHER_SIZE - LAUNCHER_MARGIN);
  return {
    right: Math.min(Math.max(position.right, LAUNCHER_MARGIN), maxRight),
    bottom: Math.min(Math.max(position.bottom, LAUNCHER_MARGIN), maxBottom),
  };
}

export function getLauncherPosition(applicantId: string): LauncherPosition {
  try {
    const raw = window.localStorage.getItem(launcherPositionKey(applicantId));
    if (raw) {
      const parsed = JSON.parse(raw) as LauncherPosition;
      if (typeof parsed.right === 'number' && typeof parsed.bottom === 'number') return clampLauncherPosition(parsed);
    }
  } catch {
    // ignore corrupt storage
  }
  return { right: 20, bottom: 20 };
}

export function setLauncherPosition(applicantId: string, position: LauncherPosition) {
  try {
    window.localStorage.setItem(launcherPositionKey(applicantId), JSON.stringify(position));
  } catch {
    // storage unavailable; ignore
  }
}

export function getChatState(applicantId: string): KadieChatState | null {
  try {
    const raw = window.localStorage.getItem(chatStateKey(applicantId));
    if (!raw) return null;
    return JSON.parse(raw) as KadieChatState;
  } catch {
    return null;
  }
}

export function setChatState(applicantId: string, state: KadieChatState) {
  try {
    window.localStorage.setItem(chatStateKey(applicantId), JSON.stringify(state));
  } catch {
    // storage unavailable; ignore
  }
}

export function clearChatState(applicantId: string) {
  try {
    window.localStorage.removeItem(chatStateKey(applicantId));
  } catch {
    // ignore
  }
}

export function getSessionLoginTimestamp(applicantId: string): number {
  try {
    const raw = window.sessionStorage.getItem(loginTimestampKey(applicantId));
    if (raw) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  } catch {
    // ignore
  }
  const timestamp = Date.now();
  try {
    window.sessionStorage.setItem(loginTimestampKey(applicantId), String(timestamp));
  } catch {
    // ignore
  }
  return timestamp;
}

interface PreviewState {
  shown: boolean;
  dismissed: boolean;
}

export function getPreviewState(applicantId: string, loginTimestamp: number): PreviewState {
  try {
    const raw = window.sessionStorage.getItem(previewStateKey(applicantId, loginTimestamp));
    if (raw) return JSON.parse(raw) as PreviewState;
  } catch {
    // ignore
  }
  return { shown: false, dismissed: false };
}

export function setPreviewState(applicantId: string, loginTimestamp: number, state: PreviewState) {
  try {
    window.sessionStorage.setItem(previewStateKey(applicantId, loginTimestamp), JSON.stringify(state));
  } catch {
    // ignore
  }
}
